import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Toast } from 'primereact/toast';
import { Table } from "ui-kit/Table";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { useTracks } from "./hooks/useTracks";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Filters } from "components/Tracks/Filters";
import IntlStore from 'stories/IntlStore';
import { IFilters, ITrack } from "./hooks/useTracks";
import { FormattedMessage } from "react-intl";
import { ProgressSpinner } from 'primereact/progressspinner';
import AudioService from "../../core/services/audioService";

export const Tracks = () => {
  const [search, setSearch] = useState<string>('');
  const [first, setFirst] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(30);
  const [filterArtistIds, setFilterArtistIds] = useState<string[]>([]);
  const [filterLabelIds, setFilterLabelIds] = useState<string[]>([]);
  const [trackTitle, setTrackTitle] = useState('');
  const [isrc, setISRC] = useState('');
  const { tracks, total, fetching } = useTracks(currentPage, pageCount, filterArtistIds, filterLabelIds, trackTitle, isrc);

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  // Audio: cache per-track stream URLs and control a single HTMLAudioElement
  const { getAudioData } = useMemo(() => AudioService(), []);
  const audioUrlsRef = useRef<Record<string, string>>({});
  const audioInflightRef = useRef<Record<string, Promise<string> | null>>({});
  const audioLruRef = useRef<string[]>([]);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const [playingId, setPlayingId] = useState<string | null>(null);
  const playingIdRef = useRef<string | null>(null);
  const [isPlayingActive, setIsPlayingActive] = useState<boolean>(false);
  const [playLoadingId, setPlayLoadingId] = useState<string | null>(null);

  // Inject playback state into track objects to force DataTable row re-renders
  const tracksWithState = useMemo(() => {
    return (tracks || []).map((track: ITrack) => {
      const isCurrent = !!playingId && String(playingId) === String(track.id);
      const isLoading = !!playLoadingId && String(playLoadingId) === String(track.id);
      const isPlaying = isCurrent && !!isPlayingActive;

      return {
        ...track,
        _isCurrent: isCurrent,
        _isLoading: isLoading,
        _isPlaying: isPlaying,
      };
    });
  }, [tracks, playingId, playLoadingId, isPlayingActive]);

  const searchedTracks = useMemo(() => {
    return search
      ? tracksWithState.filter((track: any) => track.title.toLowerCase().includes(search.toLowerCase()))
      : tracksWithState;
  }, [search, tracksWithState]);

  const toast = useRef<any>(null);
  const isUnmountedRef = useRef(false);
  // Force re-render of the table when playback state changes
  const [, forceUpdate] = useState({});

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setCurrentPage(event.page + 1);
    setFirst(event.rows * event.page);
    setPageCount(event.rows);
  };

  const onSelectFilter = (filter: IFilters) => {
    filter.filterArtistIds && setFilterArtistIds(filter.filterArtistIds);
    filter.filterLabelIds && setFilterLabelIds(filter.filterLabelIds);
    filter.trackTitle && setTrackTitle(filter.trackTitle);
    filter.isrc && setISRC(filter.isrc);
  };

  const onResetFilters = () => {
    setFilterArtistIds([]);
    setFilterLabelIds([]);
    setTrackTitle('');
    setISRC('');
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    if ((mql as any).addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    (mql as any).addListener?.(onChange);
    return () => (mql as any).removeListener?.(onChange);
  }, []);

  const visibleTrackIds = useMemo(() => {
    return (Array.isArray(tracks) ? tracks : [])
      .map((t: any) => String(t?.id ?? ""))
      .filter(Boolean);
  }, [tracks]);

  useEffect(() => {
    isUnmountedRef.current = false;
    audioElRef.current = new Audio();
    const el = audioElRef.current;
    el.preload = "auto";
    
    const handlePlay = () => {
      setIsPlayingActive(true);
      forceUpdate({});
    };
    const handlePause = () => {
      setIsPlayingActive(false);
      forceUpdate({});
    };
    const handleEnded = () => {
      setPlayingId(null);
      playingIdRef.current = null;
      setIsPlayingActive(false);
      forceUpdate({});
    };

    el.addEventListener("play", handlePlay);
    el.addEventListener("pause", handlePause);
    el.addEventListener("ended", handleEnded);

    return () => {
      isUnmountedRef.current = true;
      el.removeEventListener("play", handlePlay);
      el.removeEventListener("pause", handlePause);
      el.removeEventListener("ended", handleEnded);
      try { el.pause(); } catch { /* noop */ }
      audioElRef.current = null;
      // cleanup any created object URLs
      Object.values(audioUrlsRef.current).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      });
      audioUrlsRef.current = {};
      audioInflightRef.current = {};
      audioLruRef.current = [];
    };
  }, []);

  const touchLru = (tid: string) => {
    const arr = audioLruRef.current;
    const idx = arr.indexOf(tid);
    if (idx >= 0) arr.splice(idx, 1);
    arr.push(tid);
    const MAX = 10;
    while (arr.length > MAX) {
      const evict = arr.shift();
      if (!evict) break;
      const url = audioUrlsRef.current[evict];
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        delete audioUrlsRef.current[evict];
      }
    }
  };

  const ensureAudioUrl = async (tid: string) => {
    if (audioUrlsRef.current[tid]) {
      touchLru(tid);
      return audioUrlsRef.current[tid];
    }
    if (audioInflightRef.current[tid]) {
      const url = await audioInflightRef.current[tid]!;
      touchLru(tid);
      return url;
    }
    const p = (async () => {
      const res = await getAudioData(`tracks/${tid}/stream`);
      const audioBlob = new Blob([res.data], { type: "audio/wav" });
      const url = URL.createObjectURL(audioBlob);
      if (isUnmountedRef.current) {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        throw new Error("unmounted");
      }
      audioUrlsRef.current[tid] = url;
      touchLru(tid);
      return url;
    })();
    audioInflightRef.current[tid] = p;
    try {
      return await p;
    } finally {
      audioInflightRef.current[tid] = null;
    }
  };

  const togglePlay = async (track: any) => {
    const tid = String(track?.id ?? "");
    if (!tid) return;
    const el = audioElRef.current;
    if (!el) return;

    const currentId = playingIdRef.current;

    // Toggle pause/play on same track
    if (currentId === tid) {
      if (el.paused) {
        try { await el.play(); } catch { /* noop */ }
      } else {
        try { el.pause(); } catch { /* noop */ }
      }
      return;
    }

    // New track or first click
    setPlayLoadingId(tid);
    forceUpdate({}); // Force UI to show loader immediately

    try {
      try { el.pause(); } catch { /* noop */ }
      setPlayingId(null);
      playingIdRef.current = null;
      setIsPlayingActive(false);

      const url = await ensureAudioUrl(tid);
      if (isUnmountedRef.current) return;

      if (el.src !== url) {
        el.src = url;
      }
      await el.play();
      
      setPlayingId(tid);
      playingIdRef.current = tid;
      setIsPlayingActive(true);
      forceUpdate({}); // Force UI to show pause icon
    } catch (e) {
      console.error("[Tracks] play failed", e);
    } finally {
      if (!isUnmountedRef.current) {
        setPlayLoadingId(null);
        forceUpdate({}); // Force UI to hide loader
      }
    }
  };

  // Revoke URLs for tracks that are no longer on this page (simple hygiene; no background prefetch).
  useEffect(() => {
    if (!visibleTrackIds.length) return;
    const currentSet = new Set(visibleTrackIds);
    Object.keys(audioUrlsRef.current).forEach((tid) => {
      if (!currentSet.has(tid)) {
        try { URL.revokeObjectURL(audioUrlsRef.current[tid]); } catch { /* noop */ }
        delete audioUrlsRef.current[tid];
      }
    });
  }, [visibleTrackIds]);

  // --- Table columns + play button are kept here (no separate columns file) ---

  const columns = [
    {
      field: 'title',
      header: 'Name',
      body: (data: any) => {
        const isCurrent = data._isCurrent;
        const isLoading = data._isLoading;
        const isPlaying = data._isPlaying;

        return (
          <NameContainer>
            <PlayButton
              type="button"
              disabled={isLoading && !isCurrent}
              onClick={(e) => {
                e.stopPropagation();
                togglePlay(data);
              }}
            >
              {isLoading ? (
                <LoadingDot />
              ) : isPlaying ? (
                // PAUSE ICON
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#6777FF" strokeWidth="2" />
                  <rect x="8" y="7" width="3" height="10" rx="1" fill="#6777FF" />
                  <rect x="13" y="7" width="3" height="10" rx="1" fill="#6777FF" />
                </svg>
              ) : (
                // PLAY ICON
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" stroke="#6777FF" strokeWidth="2" />
                  <path d="M10 8L16 12L10 16V8Z" fill="#6777FF" />
                </svg>
              )}
            </PlayButton>
            <div style={{ fontWeight: 500 }}>{data.title}</div>
          </NameContainer>
        );
      }
    },
    {
      field: 'artist',
      header: 'Artist',
      body: (data: ITrack) => <div>{data.artist}</div>
    },
    {
      field: 'isrc',
      header: 'ISRC',
      body: (data: ITrack) => (
        <div
          style={{ cursor: 'pointer', color: '#6777FF' }}
          onClick={() => {
            navigator.clipboard.writeText(data.isrc);
            toast.current?.show({
              severity: 'success',
              summary: IntlStore.intl.formatMessage({
                id: "Tracks.Success",
                defaultMessage: "Success"
              }),
              detail: IntlStore.intl.formatMessage({
                id: "Tracks.SuccessCopied",
                defaultMessage: "ISRC copied successfully"
              })
            });
          }}
        >
          {data.isrc}
        </div>
      )
    },
    {
      field: 'releases_count',
      header: 'Releases Count',
      body: (data: ITrack) => <div>{data.releases_count}</div>
    }
  ];

  return (
    <Container>
          <TopBar>
            <FilterContainer>
              <SearchInput
                name="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={IntlStore.intl.formatMessage({
                  id: "Tracks.Search",
                  defaultMessage: "Search by tracks"
                })}
              />
              <Filters
                filterArtistIds={filterArtistIds}
                filterLabelIds={filterLabelIds}
                trackTitle={trackTitle}
                isrc={isrc}
                onSelectFilter={onSelectFilter}
                onResetFilters={onResetFilters}
              />
            </FilterContainer>
          </TopBar>

          <ScrollableList>
            {fetching && (
              <ListLoading>
                <ProgressSpinner style={{ width: 34, height: 34 }} strokeWidth="6" />
              </ListLoading>
            )}
            <Table
              total={total}
              pageCount={pageCount}
              currentPage={first}
              columns={columns}
              data={searchedTracks}
              onPageChange={onPageChange}
              hidePaginator
            />
            <Toast ref={toast} />
          </ScrollableList>

          <PaginatorBar>
            <PaginatorWrapper>
              <Paginator
                first={first}
                rows={pageCount}
                totalRecords={total}
                rowsPerPageOptions={[5, 10, 20, 30]}
                onPageChange={onPageChange}
                pageLinkSize={isMobile ? 1 : 5}
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              />
            </PaginatorWrapper>
          </PaginatorBar>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 28px 20px 18px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const SearchInput = styled(Input)`
  height: 43px;
  width: 333px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
`;

const TopBar = styled.div`
  flex: 0 0 auto;
  background: transparent;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ScrollableList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  position: relative;
`;

const ListLoading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 2;
`;

const PaginatorBar = styled.div`
  flex: 0 0 auto;
  width: calc(100% + 40px);
  margin: 0 -20px; /* full-bleed (cancel Container padding) */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  .p-paginator {
    width: 100%;
    display: flex;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    
    flex-wrap: nowrap;
    align-items: center;
  }

  .p-paginator-pages {
    white-space: nowrap;
  }

  .p-paginator-first,
  .p-paginator-prev,
  .p-paginator-next,
  .p-paginator-last {
    flex: 0 0 auto;
  }

  .p-paginator-rpp-options,
  .p-paginator .p-dropdown {
    position: absolute;
    right: 8px;
  }

  @media (max-width: 768px) {
    .p-paginator .p-paginator-pages .p-paginator-page,
    .p-paginator-first,
    .p-paginator-prev,
    .p-paginator-next,
    .p-paginator-last {
      min-width: 32px;
      height: 32px;
    }
  }
`;

const PaginatorWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlayButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LoadingDot = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #6777FF;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
